import * as React from "react"
import { Link as RouterLink } from "react-router-dom"
import { Copy, Filter, Plus, Upload } from "lucide-react"
import { ExpenseList } from "@/components/expenses/expense-list"
import { TransactionList } from "@/components/expenses/transaction-list"
import { ExpenseForm } from "@/components/expenses/expense-form"
import { SettlementForm } from "@/components/settlements/settlement-form"
import { PoolInsertForm } from "@/components/pools/pool-insert-form"
import { Button } from "@/components/ui/button"
import { ResponsiveContainer } from "@/components/layout/responsive-grid"
import { LoadingState } from "@/components/ui/loading-state"
import { useAuthStore } from "@/stores/auth.store"
import { useExpenses } from "@/hooks/queries/useExpenses"
import { useTransactions } from "@/hooks/queries/useTransactions"
import { useGroupCategories } from "@/hooks/queries/useCategories"
import { useGroupUsers } from "@/hooks/queries/useGroups"
import { useActiveGroup } from "@/stores/group.store"
import { useCategoryBalances } from "@/hooks/queries/useCategoryBalances"
import { useCreateOfflineExpense } from "@/hooks/offline/useOfflineExpenses"
import { useCreateSettlement } from "@/hooks/mutations/useCreateSettlement"
import { useCreatePoolInsert } from "@/hooks/mutations/useCreatePoolInsert"
import type { Expense } from "@/types/domain.types"
import { useExpenseForm } from "@/contexts/expense-form.context"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import { QUERY_KEYS } from "@/lib/constants"
import * as Effect from 'effect/Effect'
import { pipe } from 'effect/Function'
import { ApiClient } from '@/api/client'
import { MainLayer } from '@/effects/layers'
import { runEffect } from '@/effects/runtime'
import { logger } from '@/lib/logger'
import { useFundingPools } from "@/hooks/queries/useFundingPools"

// Note: Effect imports are used by the offline sync logic in useEffect


export const ExpensesPage: React.FC = () => {
  logger.debug('[ExpensesPage] Component rendering');

  // Set up sync when coming back online
  React.useEffect(() => {
    const syncOfflineExpenses = async () => {
      const offlineExpenses = JSON.parse(localStorage.getItem('offline-expenses') || '[]');
      if (offlineExpenses.length === 0) return;

      // Filter out already syncing or synced expenses
      const expensesToSync = offlineExpenses.filter((e: any) =>
        !e.syncing && !e.syncedAt
      );

      if (expensesToSync.length === 0) {
        logger.debug('[ExpensesPage] No expenses need syncing');
        return;
      }

      logger.debug(`[ExpensesPage] Syncing ${expensesToSync.length} offline expenses...`);

      const syncedIds: string[] = [];
      const failedIds: string[] = [];

      // Mark expenses as syncing to prevent duplicate syncs
      const updatedExpenses = offlineExpenses.map((e: any) => {
        if (expensesToSync.some((toSync: any) => toSync.id === e.id)) {
          return { ...e, syncing: true };
        }
        return e;
      });
      localStorage.setItem('offline-expenses', JSON.stringify(updatedExpenses));

      // Sync each expense to the API
      for (const expense of expensesToSync) {
        try {
          logger.debug('[ExpensesPage] Syncing expense:', expense);

          // Skip if no category (backend requires it)
          if (!expense.category_id || expense.category_id === '') {
            logger.warn('[ExpensesPage] Skipping expense without category:', expense);
            continue;
          }

          // Use ApiClient with Effect
          const program = pipe(
            Effect.gen(function* () {
              const apiClient = yield* ApiClient;
              const expenseDate = expense.created_at || expense.date;

              const payload = {
                group: expense.group_id,
                category: expense.category_id,
                description: expense.description || '',
                amount: Number(expense.amount),
                currency_code: expense.currency_code,
                paid_by: expense.paid_by,
                funding_pool_id: expense.funding_pool_id,
                use_group_pool: expense.use_group_pool,
                date: expenseDate ? new Date(expenseDate) : undefined,
              };

              logger.debug('[ExpensesPage] Sending payload:', { payload });
              const result = yield* apiClient.post('/expenses/create', payload);
              return result;
            }),
            Effect.provide(MainLayer)
          );

          const result = await runEffect(program);

          if (result) {
            logger.debug('[ExpensesPage] Successfully synced expense:', { expenseId: expense.id, result });
            syncedIds.push(expense.id);
            toast.success(`Synced expense: ${expense.description}`);
          }
        } catch (error) {
          logger.error('[ExpensesPage] Error syncing expense:', { error });
          failedIds.push(expense.id);
          // Continue trying to sync other expenses
        }
      }

      // Update localStorage with sync results
      if (syncedIds.length > 0 || failedIds.length > 0) {
        const currentExpenses = JSON.parse(localStorage.getItem('offline-expenses') || '[]');
        const remainingExpenses = currentExpenses.map((e: any) => {
          if (syncedIds.includes(e.id)) {
            // Mark as synced with timestamp and server ID if available
            return { ...e, syncing: false, syncedAt: new Date().toISOString(), synced: true };
          } else if (failedIds.includes(e.id)) {
            // Mark as failed, remove syncing flag so it can retry
            return { ...e, syncing: false, syncError: true };
          }
          return e;
        }).filter((e: any) => !e.syncedAt); // Remove fully synced expenses
        localStorage.setItem('offline-expenses', JSON.stringify(remainingExpenses));

        // Invalidate queries to refresh the UI
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPENSES] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRANSACTIONS] });

        logger.debug(`[ExpensesPage] Synced ${syncedIds.length} expenses, ${remainingExpenses.length} remaining`);
      }
    };

    // Check if we're online and sync
    const handleOnline = () => {
      logger.debug('[ExpensesPage] Came back online, syncing...');
      syncOfflineExpenses();
    };

    window.addEventListener('online', handleOnline);

    // Also check on mount if we're online
    const isForceOffline = localStorage.getItem('forceOffline') === 'true';
    if (navigator.onLine && !isForceOffline) {
      logger.debug('[ExpensesPage] Online on mount, checking for offline expenses to sync');
      syncOfflineExpenses();
    } else {
      logger.debug('[ExpensesPage] Offline on mount, skipping sync', { navigatorOnline: navigator.onLine, forceOffline: isForceOffline });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);
  const { user } = useAuthStore()
  const { activeGroupId } = useActiveGroup()
  const queryClient = useQueryClient()
  const { isOpen: showExpenseForm, openAddExpenseForm, closeExpenseForm } = useExpenseForm()
  const [editingExpense, setEditingExpense] = React.useState<Expense | undefined>()
  const [viewMode, setViewMode] = React.useState<'expenses' | 'transactions'>('transactions')
  const [showSettlementForm, setShowSettlementForm] = React.useState(false)
  const [showPoolInsertForm, setShowPoolInsertForm] = React.useState(false)
  const [showFilters, setShowFilters] = React.useState(false)
  const createExpenseMutation = useCreateOfflineExpense()
  const createSettlementMutation = useCreateSettlement(activeGroupId || "")
  const createPoolInsertMutation = useCreatePoolInsert(activeGroupId || "")

  // Show prompt if no group is selected
  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">No Group Selected</h3>
          <p className="text-muted-foreground mb-4">
            Please select a group from the dropdown above to view and manage expenses.
          </p>
        </div>
      </ResponsiveContainer>
    )
  }

  // Fetch real data from API with infinite scroll
  const {
    data: expensesData,
    isLoading: isLoadingExpenses,
    error: expensesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useExpenses(activeGroupId || "")

  // Fetch combined transactions (expenses + settlements)
  const {
    data: transactionsData,
    isLoading: isLoadingTransactions,
    error: transactionsError,
    fetchNextPage: fetchNextTransactionsPage,
    hasNextPage: hasNextTransactionsPage,
    isFetchingNextPage: isFetchingNextTransactionsPage,
  } = useTransactions(activeGroupId || "")

  // Fetch category balances for pinned categories
  const {
    data: categoryBalancesData,
    isLoading: isLoadingCategoryBalances,
    error: categoryBalancesError,
  } = useCategoryBalances(activeGroupId || "")

  // Debug logging
  React.useEffect(() => {
    console.log('[ExpensesPage] Category balances:', {
      data: categoryBalancesData,
      loading: isLoadingCategoryBalances,
      error: categoryBalancesError,
      activeGroupId
    })
  }, [categoryBalancesData, isLoadingCategoryBalances, categoryBalancesError, activeGroupId])

  // Flatten expenses from all pages
  const expenses = expensesData?.pages.flatMap((page) => page.expenses) ?? [];
  const transactions = transactionsData?.pages.flatMap((page) => page.transactions) ?? [];
  const { data: categories = [], isLoading: isLoadingCategories } = useGroupCategories(
    activeGroupId || ""
  )
  const { data: groupMembers = [], isLoading: isLoadingMembers } = useGroupUsers(
    activeGroupId || ""
  )
  const { data: fundingPools = [], isLoading: isLoadingFundingPools } = useFundingPools(
    activeGroupId || ""
  )

  const isLoading = viewMode === 'transactions'
    ? isLoadingTransactions || isLoadingCategories || isLoadingMembers || isLoadingFundingPools
    : isLoadingExpenses || isLoadingCategories || isLoadingMembers || isLoadingFundingPools

  const handleAddExpense = () => {
    setEditingExpense(undefined)
    openAddExpenseForm()
  }

  const handleAddSettlement = () => {
    setShowSettlementForm(true)
  }

  const handleInsertIntoPool = () => {
    setShowPoolInsertForm(true)
  }

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense)
    openAddExpenseForm()
  }

  const handleDeleteExpense = async (expenseId: string) => {
    // TODO: Implement delete expense
    logger.debug("Delete expense:", { expenseId })
  }

  logger.debug('hello?')

  const handleSubmitExpense = async (data: {
    description: string;
    amount: number;
    currency: string;
    date: string;
    category_id?: string;
    funding_pool_id?: string;
    use_group_pool?: boolean;
    paid_by_user_id: string;
    splits: Array<{
      user_id: string;
      amount: number;
      weight?: number;
    }>;
  }) => {
    if (!activeGroupId) {
      logger.error("Please select a group first")
      toast.error("Please select a group first")
      return
    }

    try {
      if (editingExpense) {
        // TODO: Implement update expense
        logger.debug("Update expense not yet implemented:", { data })
        toast.info("Expense updates are coming soon")
      } else {
        await createExpenseMutation.mutateAsync({
          groupId: activeGroupId,
          categoryId: data.category_id || "",
          amount: data.amount,
          description: data.description,
          paidBy: data.paid_by_user_id || user?.id || "",
          createdBy: user?.id,
          date: data.date ? new Date(data.date) : undefined,
          currency: data.currency,
          fundingPoolId: data.funding_pool_id,
          useGroupPool: data.use_group_pool,
        })
        closeExpenseForm()
      }
    } catch (error) {
      logger.error("Failed to save expense:", { error })
      toast.error("Failed to save expense")
    }
  }

  const handleSubmitSettlement = async (data: {
    from_user_id: string
    to_user_id: string
    amount: number
    currency_code: string
    description?: string
  }) => {
    await createSettlementMutation.mutateAsync(data)
  }

  const handleSubmitPoolInsert = async (data: {
    poolId?: string
    amount: number
    currency_code: string
    inserted_by?: string
    description?: string
    date?: string
  }) => {
    await createPoolInsertMutation.mutateAsync(data)
  }

  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-muted-foreground">
            Please select a group to view expenses
          </h2>
        </div>
      </ResponsiveContainer>
    )
  }

  if (isLoading) {
    return <LoadingState message="Loading expenses..." />
  }


  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-4 sm:space-y-6">
        {/* Streamlined mobile-friendly header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold">Transactions</h1>
          </div>

          {/* Action buttons - visible on desktop, hidden on mobile (FAB replaces them) */}
          <div className="hidden sm:flex gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <RouterLink to="/expenses/duplicates">
                <Copy className="mr-2 h-4 w-4" />
                Duplicates
              </RouterLink>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <RouterLink to="/expenses/import">
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </RouterLink>
            </Button>
            <Button
              onClick={handleInsertIntoPool}
              variant="outline"
              size="sm"
            >
              Insert Into Pool
            </Button>
            <Button
              onClick={handleAddSettlement}
              variant="outline"
              size="sm"
            >
              Record Settlement
            </Button>
            <Button
              onClick={handleAddExpense}
              size="sm"
            >
              Add Expense
            </Button>
          </div>

          {/* Mobile filter toggle */}
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            size="sm"
            className="sm:hidden"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Collapsible filters section */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row gap-2 py-3 px-3 -mx-3 bg-gray-50 rounded-lg sm:hidden">
            <Button
              variant={viewMode === 'transactions' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('transactions')}
              className="w-full"
            >
              All Transactions
            </Button>
            <Button
              variant={viewMode === 'expenses' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('expenses')}
              className="w-full"
            >
              Transactions Only
            </Button>
            <Button
              onClick={handleAddSettlement}
              variant="outline"
              size="sm"
              className="w-full"
            >
              Record Settlement
            </Button>
            <Button
              onClick={handleInsertIntoPool}
              variant="outline"
              size="sm"
              className="w-full"
            >
              Insert Into Pool
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full"
            >
              <RouterLink to="/expenses/duplicates">
                <Copy className="mr-2 h-4 w-4" />
                Duplicates
              </RouterLink>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full"
            >
              <RouterLink to="/expenses/import">
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </RouterLink>
            </Button>
          </div>
        )}

        {/* Desktop filters - always visible */}
        <div className="hidden sm:flex gap-2">
          <Button
            variant={viewMode === 'transactions' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('transactions')}
          >
            All Transactions
          </Button>
          <Button
            variant={viewMode === 'expenses' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('expenses')}
          >
            Expenses Only (Legacy)
          </Button>
        </div>

        {viewMode === 'transactions' ? (
          <TransactionList
            transactions={transactions}
            categoryBalances={categoryBalancesData?.categories || []}
            isLoading={isLoadingTransactions || isLoadingCategoryBalances}
            isError={!!transactionsError}
            onAddExpense={handleAddExpense}
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            onLoadMore={() => hasNextTransactionsPage && fetchNextTransactionsPage()}
            hasMore={hasNextTransactionsPage}
            isLoadingMore={isFetchingNextTransactionsPage}
            groupBy="date"
          />
        ) : (
          <ExpenseList
            expenses={expenses}
            isLoading={isLoadingExpenses}
            isError={!!expensesError}
            onAddExpense={handleAddExpense}
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            groupBy="date"
            onLoadMore={() => hasNextPage && fetchNextPage()}
            isLoadingMore={isFetchingNextPage}
            hasMore={hasNextPage}
          />
        )}

        <ExpenseForm
          open={showExpenseForm}
          onOpenChange={(open) => {
            if (!open) {
              closeExpenseForm()
              setEditingExpense(undefined)
            } else {
              openAddExpenseForm()
            }
          }}
          expense={editingExpense}
          categories={categories}
          groupMembers={groupMembers}
          fundingPools={fundingPools}
          currentUserId={user?.id || ""}
          onSubmit={handleSubmitExpense}
        />

        {showSettlementForm && (
          <SettlementForm
            open={showSettlementForm}
            onOpenChange={setShowSettlementForm}
            groupMembers={groupMembers}
            groupId={activeGroupId || ""}
            currentUserId={user?.id || ""}
            onSubmit={handleSubmitSettlement}
          />
        )}

        {showPoolInsertForm && (
          <PoolInsertForm
            open={showPoolInsertForm}
            onOpenChange={setShowPoolInsertForm}
            pools={fundingPools}
            groupMembers={groupMembers}
            currentUserId={user?.id || ""}
            onSubmit={handleSubmitPoolInsert}
          />
        )}

        {/* Mobile Floating Action Button */}
        <div className="fixed bottom-20 right-4 z-50 sm:hidden">
          <Button
            size="lg"
            onClick={handleAddExpense}
            className="h-14 w-14 rounded-full shadow-lg flex items-center justify-center p-0"
          >
            <Plus className="h-6 w-6 text-white" />
            <span className="sr-only">Add Expense</span>
          </Button>
        </div>
      </div>
    </ResponsiveContainer>
  )
}
